import React, { useEffect, useState } from "react";
import { MdClose, MdEdit, MdDelete, MdAdd } from "react-icons/md";
import { toast } from "react-toastify";
import AddCategoryModal from "./AddCategoryModal";
import EditCategoryModal from "./EditCategoryModal";

export default function CategoryTable() {
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [showEditCategory, setShowEditCategory] = useState(false);
  const [categories, setCategories] = useState([]);
  const [editableCategory, setEditableCategory] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const toggleAddCategory = () => {
    setShowAddCategory(!showAddCategory);
  };

  const toggleEditCategory = (category) => {
    setEditableCategory(category);
    setShowEditCategory(!showEditCategory);
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const handleDeleteCategory = async (categoryId) => {
    if (window.confirm("Are you sure you want to delete this category?")) {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/category/delete-category/${categoryId}`,
          { method: "DELETE" }
        );
        if (!response.ok) throw new Error("Failed to delete the category.");
        toast.success("Category deleted successfully!");
        fetchCategories();
      } catch (error) {
        console.error("Delete error:", error);
        toast.error("Error deleting the category.");
      }
    }
  };

  const fetchCategories = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/category/all-category`
      );
      if (!response.ok) throw new Error("Failed to fetch categories.");
      const data = await response.json();
      setCategories(data);
    } catch (error) {
      console.error("Fetch error:", error);
      toast.error("Error fetching categories.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-800">Categories</h1>
        <button
          className="hover:bg-primary-dark flex items-center rounded-md bg-primary px-4 py-2 text-primaryText transition-colors"
          onClick={toggleAddCategory}
        >
          <MdAdd className="mr-2" size={20} />
          Add Category
        </button>
      </div>

      {showAddCategory && (
        <AddCategoryModal
          toggleAddCategory={toggleAddCategory}
          showAddCategory={showAddCategory}
          onCategoryAdded={fetchCategories}
        />
      )}

      {showEditCategory && editableCategory && (
        <EditCategoryModal
          category={editableCategory}
          toggleModal={() => toggleEditCategory(null)}
          showModal={showEditCategory}
          onCategoryUpdated={fetchCategories}
          categoryId={editableCategory.id}
        />
      )}

      {/* Category Table */}
      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-primary">
            <tr>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-primaryText"
              >
                Category Name
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-primaryText"
              >
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {isLoading ? (
              <tr>
                <td colSpan="2" className="px-6 py-4 text-center">
                  <div className="flex justify-center">
                    <div className="border-t-transparent h-8 w-8 animate-spin rounded-full border-4 border-primary"></div>
                  </div>
                </td>
              </tr>
            ) : categories.length === 0 ? (
              <tr>
                <td colSpan="2" className="px-6 py-4 text-center text-gray-500">
                  No categories found. Add your first category!
                </td>
              </tr>
            ) : (
              categories.map((category) => (
                <tr key={category.id} className="hover:bg-gray-50">
                  <td className="whitespace-nowrap px-6 py-4">
                    <div className="flex items-center">
                      <div className="text-base font-medium text-gray-900">
                        {category.name}
                      </div>
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-right text-sm font-medium">
                    <div className="flex justify-end space-x-2">
                      <button
                        onClick={() => toggleEditCategory(category)}
                        className="rounded-md p-2 text-gray-600 transition-colors hover:bg-gray-100 hover:text-primary"
                        title="Edit"
                      >
                        <MdEdit size={18} />
                      </button>
                      <button
                        onClick={() => handleDeleteCategory(category.id)}
                        className="rounded-md p-2 text-gray-600 transition-colors hover:bg-gray-100 hover:text-red-600"
                        title="Delete"
                      >
                        <MdDelete size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}